/**
 * lib/version.ts
 * ------------------------------------------------------------------
 * Client build identity + staleness check for the version gate
 * (slice 1: hard save-path block). `__APP_BUILD__` is injected at build
 * time by vite `define`; it is the literal string 'dev' in local dev, so
 * the gate is inert there. The deployed build id lives in the Firestore
 * doc `appConfig/version` and is watched by DataContext as `serverBuild`.
 * ------------------------------------------------------------------
 */

import { isBehind } from '@/lib/updatePolicy';

/** The build id baked into THIS client bundle at build time. */
export const APP_BUILD: string = __APP_BUILD__;

/**
 * True only when we KNOW this client is behind the deployed build.
 *
 * Deliberate FAIL-OPEN on unknown: when serverBuild is null/empty (the
 * appConfig/version listener has not resolved yet, the client is offline,
 * or the doc is unset) we return false = "not stale" = saving ALLOWED.
 * Blocking on unknown would break saves for slow or offline clients, which
 * is worse than the rare race it would prevent. We only block when
 * serverBuild is KNOWN and differs from this bundle's build. Dev builds
 * ('dev') never block.
 */
export function isClientStale(serverBuild: string | null | undefined): boolean {
  // DIRECTIONAL: only stale when THIS bundle is strictly OLDER than the
  // deployed build. The old code flagged on ANY mismatch, so if the
  // PUBLISHED id lagged the deployed bundle (a set-app-version step failed,
  // or a client loaded between the hosting deploy and the publish), every
  // up-to-date client got a "new version" banner that reloading could never
  // clear. Unknown ids fail open. The rule and its tests: lib/updatePolicy.
  return isBehind(APP_BUILD, serverBuild) === true;
}
